export interface LoadedPhoto {
  dataUrl: string
  width: number
  height: number
}

/**
 * THE PROJECT PHOTO. Downscaled on import, and this is the file that gets
 * saved, drawn, previewed and exported for the rest of the job's life.
 *
 * It is NOT the camera original. A phone photograph is 12 to 50 megapixels;
 * that is far more than a texted mockup needs and enough to make an iPad
 * stutter, so the import reduces it once and keeps the reduction. Anything
 * that says "the original photo" in this codebase means THIS - see
 * docs/PHOTO-ASSETS.md, which sets out the three assets and which is which.
 *
 * Two limits, and the second is the one that usually bites.
 */

/**
 * THE LONG EDGE, NOT THE WIDTH.
 *
 * This used to be a maximum WIDTH, which quietly treated the two orientations
 * differently: a 3840x2160 landscape shot came in at 3200x1800, while the same
 * camera held upright at 2160x3840 was never touched by the width rule at all
 * and came out 1837x3266 - taller than the limit the landscape one was held
 * to. A contractor turning his phone sideways got a different photograph.
 *
 * Measuring the long edge applies one number to both orientations. Nothing is
 * cropped and nothing is rotated: the scale is applied to both dimensions
 * together, so composition and aspect ratio are exactly what came off the
 * camera.
 */
const MAX_LONG_EDGE = 3200

/**
 * The most pixels we will put in one canvas.
 *
 * A long-edge limit alone is not enough. Browsers cap the total AREA of a
 * canvas, and past that cap toDataURL does not throw, it quietly returns the
 * five character string "data:,". That sails through every check, gets stored
 * as a perfectly good looking photo, and then never decodes - which is a
 * document with a photo in it, a black canvas, and no error anywhere. Exactly
 * the "it is all black and nothing is working" that got reported.
 *
 * For every ordinary phone photograph this is the limit that actually binds,
 * not the long edge: a 12MP shot comes in at about 2828x2121.
 */
const MAX_PIXELS = 6_000_000

/**
 * The import sizing rule, on its own, so it can be tested without a browser.
 *
 * Returns the size the PROJECT PHOTO will have. Both dimensions are scaled by
 * the same factor, so the aspect ratio is preserved and the picture is never
 * cropped; whichever way round the photograph is, the same rule applies to it.
 */
export function importedSize(
  naturalWidth: number,
  naturalHeight: number,
  maxLongEdge = MAX_LONG_EDGE,
  maxPixels = MAX_PIXELS,
): { width: number; height: number; scale: number; limitedBy: 'nothing' | 'long edge' | 'area' } {
  const longEdge = Math.max(naturalWidth, naturalHeight)
  const byLongEdge = maxLongEdge / longEdge
  const byArea = Math.sqrt(maxPixels / (naturalWidth * naturalHeight))
  const scale = Math.min(1, byLongEdge, byArea)
  return {
    width: Math.max(1, Math.round(naturalWidth * scale)),
    height: Math.max(1, Math.round(naturalHeight * scale)),
    scale,
    limitedBy: scale === 1 ? 'nothing' : byLongEdge <= byArea ? 'long edge' : 'area',
  }
}

export async function loadPhotoFile(file: File, maxLongEdge = MAX_LONG_EDGE): Promise<LoadedPhoto> {
  const url = URL.createObjectURL(file)
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image()
      i.onload = () => resolve(i)
      i.onerror = () => reject(new Error('That file could not be read as an image.'))
      i.src = url
    })

    if (!img.naturalWidth || !img.naturalHeight) {
      throw new Error('That image has no size the browser can read.')
    }
    const { width, height } = importedSize(img.naturalWidth, img.naturalHeight, maxLongEdge)

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')!
    // Best available downscaling. A cheap resample is where softness creeps in.
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(img, 0, 0, width, height)

    const dataUrl = canvas.toDataURL('image/jpeg', 0.95)
    /*
     * Check what came back rather than trusting it. A failed encode returns
     * "data:," and an out of memory one can return an empty string; both look
     * like a photo to every line of code downstream.
     */
    if (!dataUrl.startsWith('data:image/') || dataUrl.length < 1000) {
      throw new Error('That photo was too large for this browser to process. Try a smaller one.')
    }
    return { dataUrl, width, height }
  } finally {
    URL.revokeObjectURL(url)
  }
}
